/**
 * venueCache.ts
 *
 * WHY localStorage INSTEAD OF sessionStorage:
 * sessionStorage clears when the app process is killed (native app swipe-away).
 * Every cold start hit Supabase — users saw "Loading deals..." for 1-2 seconds
 * on every reopen.
 *
 * localStorage persists across restarts. With a 10-minute TTL, a user who
 * reopens the app within 10 minutes sees data instantly, then a background
 * refresh silently updates it. After 10 minutes, it fetches fresh on open.
 *
 * This is the stale-while-revalidate pattern:
 * 1. Return stale data immediately (fast)
 * 2. Fetch fresh data in background
 * 3. Update state when fresh data arrives
 *
 * CACHE_VERSION: increment if the Venue schema changes to auto-invalidate.
 */

const CACHE_TTL_MS   = 10 * 60 * 1000  // 10 minutes
const STALE_TTL_MS   = 60 * 60 * 1000  // 1 hour — still usable but stale
const CACHE_PREFIX   = 'hhu_venues_'
const CACHE_VERSION  = 2  // bumped from 1 (sessionStorage) to force fresh fetch

interface CacheEntry<T> {
  data: T
  fetchedAt: number  // when data was fetched
  expires: number    // after this, data is stale (but still usable)
  staleUntil: number // after this, discard entirely
  version: number
}

function getCacheKey(city: string): string {
  return `${CACHE_PREFIX}${city.toLowerCase().replace(/\s+/g, '_')}`
}

function readCache<T>(city: string): { data: T; isStale: boolean } | null {
  try {
    const raw = localStorage.getItem(getCacheKey(city))
    if (!raw) return null
    const entry: CacheEntry<T> = JSON.parse(raw)
    if (entry.version !== CACHE_VERSION) return null
    if (Date.now() > entry.staleUntil) {
      localStorage.removeItem(getCacheKey(city))
      return null
    }
    const isStale = Date.now() > entry.expires
    return { data: entry.data, isStale }
  } catch {
    return null
  }
}

function writeCache<T>(city: string, data: T): void {
  try {
    const now = Date.now()
    const entry: CacheEntry<T> = {
      data,
      fetchedAt: now,
      expires: now + CACHE_TTL_MS,
      staleUntil: now + STALE_TTL_MS,
      version: CACHE_VERSION,
    }
    localStorage.setItem(getCacheKey(city), JSON.stringify(entry))
  } catch (e: any) {
    if (e?.name === 'QuotaExceededError') clearVenueCache()
  }
}

export function invalidateVenueCache(city: string): void {
  try { localStorage.removeItem(getCacheKey(city)) } catch {}
}

export function clearVenueCache(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX))
    keys.forEach(k => localStorage.removeItem(k))
  } catch {}
}

/**
 * Fetch venues with stale-while-revalidate caching.
 *
 * - Fresh cache (< 10 min): return immediately, no fetch
 * - Stale cache (10 min – 1 hr): return stale data immediately,
 *   then fetch fresh in background and call onRefresh when done
 * - No cache or expired (> 1 hr): fetch fresh, show loading state
 *
 * @param city         City name
 * @param fetcher      Real fetch function (getVenues from venueService)
 * @param forceRefresh Skip cache entirely (pull-to-refresh)
 * @param onRefresh    Called with fresh data after a background revalidation
 */
export async function cachedGetVenues<T>(
  city: string,
  fetcher: (city: string) => Promise<T>,
  forceRefresh = false,
  onRefresh?: (data: T) => void,
): Promise<T> {
  if (!forceRefresh) {
    const cached = readCache<T>(city)
    if (cached !== null) {
      if (!cached.isStale) {
        // Fresh — return immediately
        return cached.data
      }
      // Stale — return old data immediately and revalidate in background
      if (onRefresh) {
        fetcher(city).then(fresh => {
          writeCache(city, fresh)
          onRefresh(fresh)
        }).catch(() => {/* silent — stale data is still showing */})
      }
      return cached.data
    }
  }

  // No usable cache — fetch and wait
  const data = await fetcher(city)
  writeCache(city, data)
  return data
}

export function hasVenueCache(city: string): boolean {
  return readCache(city) !== null
}
